// Core React import for using JSX and component structure
import React from 'react';

// React Router imports for defining client-side navigation structure
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Import the layout component that wraps all routed pages
import './App.css';
import ApiClientConfig from './components/ApiClientConfig';
import AppLayout from './components/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionGate from './components/PermissionGate';
import PublicPageShell from './components/PublicPageShell';
import { ROUTES } from './config/permissions';
import { useDocumentTitle } from './hooks/useDocumentTitle';

// --- Auth & public pages ---
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import HowToPage from './pages/HowToPage';

// HowToPage is only used via public route (/how-to + PublicPageShell), not COMPONENTS.

// --- Page Views (for componentKey lookup) ---
import Home from './pages/Home';
import HelpVideosPage from './pages/HelpVideosPage';
import CRForm from './pages/CRForm';
import CRDashboard from './pages/CRDashboard';
import ReviewPage from './pages/ReviewPage';
import DynamicEntryPage from './pages/DynamicEntryPage';
import ThankYouPage from './pages/ThankYouPage';
import GraphsPage from './pages/GraphsPage';
import InventoryPage from './pages/InventoryPage';
import InventoryHistoryPage from './pages/InventoryHistoryPage';
import StepStudyDashboard from './pages/StepStudyDashboard';
import StepStudyNewCohort from './pages/StepStudyNewCohort';
import StepStudyCohortPage from './pages/StepStudyCohortPage';
import AccessibilityPage from './pages/AccessibilityPage';
import FaqPage from './pages/FaqPage';
import InvitePage from './pages/InvitePage';
import MinistryCheckupPage from './pages/MinistryCheckupPage';
import MinistryCheckupNewPage from './pages/MinistryCheckupNewPage';
import MinistryCheckupTakePage from './pages/MinistryCheckupTakePage';
import MinistryCheckupCompletionPage from './pages/MinistryCheckupCompletionPage';
import MinistryCheckupResultsPage from './pages/MinistryCheckupResultsPage';
import MeetingsPage from './pages/MeetingsPage';
import HotIssueCapturePage from './pages/HotIssueCapturePage';
import OutreachPage from './pages/OutreachPage';
import AdminAccessPage from './pages/AdminAccessPage';
import SummitFeedbackPage from './pages/SummitFeedbackPage';
import SummitFeedbackFormPage from './pages/SummitFeedbackFormPage';
import SummitFeedbackAdminPage from './pages/SummitFeedbackAdminPage';
import SummitFeedbackResultsPage from './pages/SummitFeedbackResultsPage';
import LeadersSurveyPage from './pages/LeadersSurveyPage';
import SurveyAssignmentsPage from './pages/SurveyAssignmentsPage';
import SurveyProgressPage from './pages/SurveyProgressPage';
import SurveyEditsPage from './pages/SurveyEditsPage';
import SurveyResultsPage from './pages/SurveyResultsPage';
import SectionPage from './pages/SectionPage';

function DocumentTitleSync() {
  useDocumentTitle();
  return null;
}

const COMPONENTS = {
  Home,
  HelpVideosPage,
  CRForm,
  CRDashboard,
  ReviewPage,
  DynamicEntryPage,
  ThankYouPage,
  GraphsPage,
  InventoryPage,
  InventoryHistoryPage,
  StepStudyDashboard,
  StepStudyNewCohort,
  StepStudyCohortPage,
  InvitePage,
  MinistryCheckupPage,
  MinistryCheckupNewPage,
  MinistryCheckupTakePage,
  MinistryCheckupCompletionPage,
  MinistryCheckupResultsPage,
  MeetingsPage,
  HotIssueCapturePage,
  OutreachPage,
  AdminAccessPage,
  SummitFeedbackPage,
  SummitFeedbackFormPage,
  SummitFeedbackAdminPage,
  SummitFeedbackResultsPage,
  LeadersSurveyPage,
  SurveyAssignmentsPage,
  SurveyProgressPage,
  SurveyEditsPage,
  SurveyResultsPage,
  SectionPage,
};

/*
 * Main App component:
 * - Uses BrowserRouter (the host must serve index.html for client-side routes)
 * - Sign-in/sign-up are public; all other routes require authentication
 * - Routes are driven by config/permissions.js ROUTES array
 */
const App = () => {
  return (
    <>
      <ApiClientConfig />
      <BrowserRouter>
      <DocumentTitleSync />
      <Routes>
        {/* Public routes (no auth required) */}
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<SignUpPage />} />
        <Route
          path="/how-to"
          element={(
            <PublicPageShell>
              <HowToPage />
            </PublicPageShell>
          )}
        />
        <Route
          path="/faqs"
          element={(
            <PublicPageShell>
              <FaqPage />
            </PublicPageShell>
          )}
        />
        <Route
          path="/accessibility"
          element={(
            <PublicPageShell>
              <AccessibilityPage />
            </PublicPageShell>
          )}
        />

        {/* Protected routes: require sign-in, wrapped by AppLayout */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            {ROUTES.map((route) => {
              const Component = COMPONENTS[route.componentKey];
              if (!Component) return null;
              let element;
              if (route.sectionPageId) {
                element = <SectionPage sectionId={route.sectionPageId} />;
              } else if (route.permission === null) {
                element = <Component />;
              } else {
                element = <PermissionGate permission={route.permission}><Component /></PermissionGate>;
              }
              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={element}
                  end={route.end ?? true}
                />
              );
            })}
          </Route>
        </Route>

        {/* Catch-all: redirect unknown paths to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </>
  );
};

// Export the App component so it can be rendered in index.js
export default App;
